import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { toast } from 'sonner';
import api, { segurosApi } from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';

const fmtCLP = (v) => `$${Math.round(v).toLocaleString('es-CL')}`;

export function EgresoAlegraDialog({ grupo, ufHoy, hoy, onClose, onDone }) {
  // Aseguradora: dropdown de proveedores sugeridos + fallback a input libre
  const [rutSel, setRutSel] = useState(grupo.rut_aseguradora || '');
  const [nombreSel, setNombreSel] = useState('');
  const [rutManual, setRutManual] = useState(grupo.rut_aseguradora || '');
  const [modoManual, setModoManual] = useState(false);
  const [sugeridos, setSugeridos] = useState([]);
  const [otros, setOtros] = useState([]);
  const [rutGuardado, setRutGuardado] = useState(grupo.rut_aseguradora || '');
  const [persistirRut, setPersistirRut] = useState(true);

  const [fechaPago, setFechaPago] = useState(hoy);
  // Iter 367 — UF de la FECHA DE PAGO elegida (el backend cobra con esa UF, no con la de hoy)
  const [ufPago, setUfPago] = useState(ufHoy || null);
  const [bancos, setBancos] = useState([]);
  const [bancoId, setBancoId] = useState('');
  const [metodo, setMetodo] = useState('transfer');
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const cuotas = useMemo(() => {
    const out = [];
    for (const p of grupo.polizas) {
      const bien = `${p.patente && !(p.bien_asegurado || '').toUpperCase().includes(p.patente.toUpperCase()) ? p.patente + ' ' : ''}${p.bien_asegurado}`;
      for (const [ym, c] of Object.entries(p.celdas)) {
        const items = ym === 'vencidas' ? (c.items || []) : [c];
        for (const it of items) {
          if (it.estado !== 'pagada') {
            out.push({ key: `${p.id}|${it.numero}`, poliza_id: p.id, numero: it.numero,
              numero_poliza: p.numero_poliza, bien,
              fecha: it.fecha, uf: it.uf, vencida: it.vencida, cuota: it.cuota });
          }
        }
      }
    }
    return out.sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [grupo]);

  // Auto-preselección: pendientes vencidas o del mes en curso
  useEffect(() => {
    const pre = {};
    const mesActual = hoy.slice(0, 7);
    cuotas.forEach((c) => { if (c.vencida || c.fecha.slice(0, 7) === mesActual) pre[c.key] = true; });
    setSel(pre);
    api.get('/contabilidad/egresos/bancos').then((r) => {
      setBancos(r.data.bancos || []);
      const chile1 = (r.data.bancos || []).find((b) => (b.name || '').toLowerCase().includes('banco de chile 1'));
      setBancoId(String(chile1?.id || r.data.principal_id || r.data.bancos?.[0]?.id || ''));
    }).catch(() => {});
  }, [cuotas, hoy]);

  // Cargar UF de la fecha de pago (fallback: UF de hoy)
  useEffect(() => {
    if (!fechaPago) return;
    if (ufHoy && fechaPago === hoy) { setUfPago(ufHoy); return; }
    let vivo = true;
    segurosApi.uf(fechaPago)
      .then((r) => { if (vivo) setUfPago(r.data); })
      .catch(() => { if (vivo) setUfPago(ufHoy || null); });
    return () => { vivo = false; };
  }, [fechaPago, hoy, ufHoy]);

  // Cargar proveedores sugeridos para la aseguradora
  useEffect(() => {
    segurosApi.proveedoresParaAseguradora(grupo.aseguradora)
      .then((r) => {
        const sug = r.data.sugeridos || [];
        const otr = r.data.otros || [];
        const guardado = r.data.rut_guardado || '';
        setSugeridos(sug);
        setOtros(otr);
        setRutGuardado(guardado);
        // Autoselección inicial: usar RUT guardado si existe, si no el primer sugerido
        if (guardado) {
          const match = [...sug, ...otr].find((x) => x.rut === guardado);
          if (match) {
            setRutSel(match.rut);
            setNombreSel(match.nombre);
          } else {
            setRutSel(guardado);
            setModoManual(true);
            setRutManual(guardado);
          }
        } else if (sug.length === 1) {
          setRutSel(sug[0].rut);
          setNombreSel(sug[0].nombre);
        }
      })
      .catch(() => {});
  }, [grupo.aseguradora]);

  const seleccionadas = cuotas.filter((c) => sel[c.key]);
  const totalUF = seleccionadas.reduce((s, c) => s + c.uf, 0);
  const totalCLP = ufPago ? Math.round(totalUF * ufPago.valor) : 0;

  const rutEfectivo = (modoManual ? rutManual : rutSel).trim();

  const opciones = useMemo(() => {
    const combined = [];
    if (sugeridos.length) {
      sugeridos.forEach((s) => combined.push({ ...s, grupo: 'sugerido' }));
    }
    if (otros.length) {
      otros.forEach((s) => combined.push({ ...s, grupo: 'otro' }));
    }
    return combined;
  }, [sugeridos, otros]);

  const seleccionarProveedor = (rut) => {
    const match = opciones.find((o) => o.rut === rut);
    if (match) {
      setRutSel(match.rut);
      setNombreSel(match.nombre);
    } else {
      setRutSel(rut);
      setNombreSel('');
    }
  };

  // Iter 367 — ¿alguna cuota seleccionada ya quedó pagada? (evita el 422 "no está pendiente"
  // cuando el egreso sí se creó pero la respuesta se perdió por timeout del proxy)
  const verificarYaPagadas = async (refs) => {
    const r = await segurosApi.estadoCuotas(refs);
    const pagadas = (r.data?.cuotas || []).filter((c) => c.estado === 'pagada');
    return { pagadas, todas: !!r.data?.todas_pagadas };
  };

  const avisarYaPagadas = (pagadas) => {
    const egresos = [...new Set(pagadas.map((c) => c.egreso_numero || c.egreso_alegra_id).filter(Boolean))];
    const det = pagadas.map((c) => `Pól. ${c.numero_poliza} cuota ${c.numero}`).join(', ');
    toast.info(
      `${det} ya ${pagadas.length > 1 ? 'están pagadas' : 'está pagada'}` +
      (egresos.length ? ` (egreso Alegra N° ${egresos.join(', ')})` : '') + '. Nada que pagar de nuevo.',
      { duration: 10000 },
    );
  };

  const confirmar = async () => {
    if (busy) return;
    if (!seleccionadas.length) { toast.warning('Selecciona al menos una cuota'); return; }
    if (!rutEfectivo) { toast.warning('Selecciona la aseguradora o indica el RUT manualmente'); return; }
    if (!bancoId) { toast.warning('Selecciona el medio de pago'); return; }
    setBusy(true);
    const refs = seleccionadas.map((c) => ({ poliza_id: c.poliza_id, numero: c.numero }));
    try {
      // Pre-chequeo contra el servidor: la lista del modal puede estar desactualizada
      try {
        const { pagadas } = await verificarYaPagadas(refs);
        if (pagadas.length) { avisarYaPagadas(pagadas); onDone?.(); return; }
      } catch { /* si falla el pre-chequeo, el backend igual valida */ }

      // Si el usuario cambió la aseguradora respecto al RUT guardado, persistir antes
      if (persistirRut && rutEfectivo && rutEfectivo !== rutGuardado) {
        try {
          await segurosApi.actualizarRutAseguradora({
            aseguradora: grupo.aseguradora,
            rut: rutEfectivo,
            razon_social: nombreSel || '',
          });
        } catch (e) {
          // No es bloqueante — solo warning; el pago igual persistirá el RUT si tiene éxito
          console.warn('No se pudo persistir el RUT antes del pago', e);
        }
      }
      const res = await segurosApi.pagarYEgreso({
        aseguradora: grupo.aseguradora,
        rut_aseguradora: rutEfectivo,
        cuotas: refs,
        bank_account_id: Number(bancoId),
        fecha_pago: fechaPago,
        payment_method: metodo,
      });
      toast.success(`✓ Egreso ${res.data.numero_egreso || res.data.payment_id} creado en Alegra por ${fmtCLP(res.data.total)} (${res.data.cuotas_pagadas} cuota(s), UF $${Number(res.data.uf_usada).toLocaleString('es-CL', { minimumFractionDigits: 2 })})`);
      onDone?.();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'Error desconocido';
      // Respuesta perdida (timeout / 502 / 504) o "no está pendiente": verificar si el egreso sí quedó
      if (!status || status >= 502 || (status === 422 && /no está pendiente/i.test(detail))) {
        try {
          const { pagadas } = await verificarYaPagadas(refs);
          if (pagadas.length) {
            avisarYaPagadas(pagadas);
            if (status === 422 || pagadas.length === refs.length) { onDone?.(); return; }
          }
        } catch { /* seguir al error genérico */ }
      }
      toast.error(`Error al registrar el pago: ${detail}`, { duration: 10000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dialog-egreso-alegra">
        <DialogHeader>
          <DialogTitle className="text-base">Registrar pago + egreso en Alegra — {grupo.aseguradora}</DialogTitle>
          <DialogDescription className="text-xs">
            Se creará UN comprobante de egreso (anticipo al proveedor por RUT) contra el medio de pago
            elegido, y las cuotas quedarán pagadas con la UF del día de pago.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1 col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Aseguradora / RUT *</Label>
              <button
                type="button"
                onClick={() => setModoManual((v) => !v)}
                className="text-[10px] text-blue-600 hover:underline"
                data-testid="btn-toggle-manual-rut"
              >
                {modoManual ? '← Elegir de la lista' : 'RUT manual →'}
              </button>
            </div>
            {!modoManual ? (
              <>
                <select
                  value={rutSel}
                  onChange={(e) => seleccionarProveedor(e.target.value)}
                  className="h-8 text-sm border rounded w-full px-2 bg-white"
                  data-testid="select-aseguradora"
                >
                  <option value="">— Selecciona la aseguradora —</option>
                  {sugeridos.length > 0 && (
                    <optgroup label={`Sugeridos para ${grupo.aseguradora}`}>
                      {sugeridos.map((o) => (
                        <option key={o.rut} value={o.rut}>{o.nombre} · {o.rut}</option>
                      ))}
                    </optgroup>
                  )}
                  {otros.length > 0 && (
                    <optgroup label="Otros que contienen el nombre">
                      {otros.map((o) => (
                        <option key={o.rut} value={o.rut}>{o.nombre} · {o.rut}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {rutSel && (
                  <div className="text-[10px] text-muted-foreground">
                    RUT seleccionado: <span className="font-mono">{rutSel}</span>
                    {nombreSel && ` · ${nombreSel}`}
                  </div>
                )}
                {sugeridos.length === 0 && otros.length === 0 && (
                  <div className="text-[10px] text-amber-600">
                    Sin coincidencias en Proveedores. Usa &quot;RUT manual →&quot; para tipear el RUT.
                  </div>
                )}
              </>
            ) : (
              <>
                <Input
                  value={rutManual}
                  onChange={(e) => setRutManual(e.target.value)}
                  placeholder="96.792.430-K"
                  className="h-8 text-sm font-mono"
                  data-testid="input-rut-aseguradora-manual"
                />
                <div className="text-[10px] text-muted-foreground">
                  Tipea el RUT como aparece en Alegra (con o sin puntos).
                </div>
              </>
            )}
            {rutEfectivo && rutEfectivo !== rutGuardado && (
              <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground pt-1">
                <Checkbox
                  checked={persistirRut}
                  onCheckedChange={(v) => setPersistirRut(!!v)}
                  data-testid="chk-persistir-rut"
                />
                Guardar este RUT en todas las pólizas de {grupo.aseguradora} (recomendado).
              </label>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Fecha de pago</Label>
            <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)}
              className="h-8 text-sm" data-testid="input-fecha-pago-egreso" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Medio de pago (cuenta Alegra) *</Label>
            <select value={bancoId} onChange={(e) => setBancoId(e.target.value)}
              className="h-8 text-sm border rounded w-full px-2 bg-white" data-testid="select-banco-egreso">
              {bancos.map((b) => <option key={b.id} value={b.id}>{b.name}{b.type === 'credit-card' ? ' (TC)' : ''}</option>)}
            </select>
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">Método</Label>
            <select value={metodo} onChange={(e) => setMetodo(e.target.value)}
              className="h-8 text-sm border rounded w-full px-2 bg-white" data-testid="select-metodo-egreso">
              <option value="transfer">Transferencia</option>
              <option value="credit-card">Tarjeta de crédito</option>
              <option value="cash">Efectivo</option>
            </select>
          </div>
        </div>

        <div className="border rounded max-h-56 overflow-y-auto">
          {cuotas.map((c) => (
            <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 border-b last:border-0 text-xs cursor-pointer hover:bg-muted/30">
              <Checkbox checked={!!sel[c.key]} onCheckedChange={(v) => setSel((s) => ({ ...s, [c.key]: !!v }))}
                data-testid={`chk-cuota-${c.numero_poliza}-${c.numero}`} />
              <span className={`font-mono ${c.vencida ? 'text-red-600 font-semibold' : ''}`}>{fmtFecha(c.fecha)}{c.vencida ? ' ⚠' : ''}</span>
              <span className="flex-1 truncate">{c.bien} · Pól. {c.numero_poliza} · {c.cuota}</span>
              <span>{c.uf.toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF</span>
              <b>{ufPago ? fmtCLP(c.uf * ufPago.valor) : ''}</b>
            </label>
          ))}
          {cuotas.length === 0 && <div className="text-center text-xs text-muted-foreground py-4">Sin cuotas pendientes</div>}
        </div>

        <div className="flex justify-between items-center text-sm">
          <span className="text-xs text-muted-foreground">
            {seleccionadas.length} cuota(s) · {totalUF.toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF
            {ufPago && (
              <span className="ml-1" data-testid="uf-fecha-pago">
                · UF ${Number(ufPago.valor).toLocaleString('es-CL', { minimumFractionDigits: 2 })} del {fmtFecha(ufPago.fecha || fechaPago)}
              </span>
            )}
          </span>
          <span className="font-bold" data-testid="total-egreso">Total: {fmtCLP(totalCLP)}</span>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="btn-cancelar-egreso">Cancelar</Button>
          <Button size="sm" onClick={confirmar} disabled={busy || !seleccionadas.length} data-testid="btn-confirmar-egreso">
            {busy ? 'Registrando…' : 'Confirmar y crear egreso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
